import React, { useEffect, useState, useCallback } from 'react';
import { Button } from 'antd';
import NotificationItem from "../../components/AppNotification/NotificationItem";
import CustomScrollbars from "../../util/CustomScrollbars";
import Auxiliary from "../../util/Auxiliary";
import axios from '../../util/Api';

const MAX_NOTIFICATIONS = 50;

const notificationKey = (notification) => notification.id || `${notification.title}-${notification.timestamp}`;

const mergeNotifications = (existing, incoming) => {
    const combined = [...incoming, ...existing];
    const seen = new Set();
    const deduped = combined.filter((notification) => {
        const key = notificationKey(notification);
        if (seen.has(key)) {
            return false;
        }
        seen.add(key);
        return true;
    });
    return deduped
        .sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0))
        .slice(0, MAX_NOTIFICATIONS);
};

// Live notifications carry a Bull queue job id, not the persisted Mongo document id -
// only the latter can be patched via the read/remove endpoints.
const isPersistedNotificationId = (id) => /^[0-9a-fA-F]{24}$/.test(id || '');

const readLiveNotifications = () => {
    try {
        return JSON.parse(localStorage.getItem('notifications') || '[]');
    } catch (e) {
        return [];
    }
};

// Live records carry a `target` like "<organisationId>-<email>" - the org id is the
// segment before the first '-'. Used to scope the bell to the current organisation.
const notificationOrgId = (notification) => {
    const target = notification.target || '';
    const idx = target.indexOf('-');
    return idx > 0 ? target.slice(0, idx) : null;
};

// Only show live notifications addressed to the logged-in user's organisation.
// Records without a target predate org-scoping, so keep them for backward compat.
const belongsToOrg = (notification, organisationId) => {
    const orgId = notificationOrgId(notification);
    return !orgId || orgId === organisationId;
};

// Keeps the raw SSE-sourced localStorage list in sync with remove/read actions,
// otherwise the next live notification would re-merge stale/removed entries back in.
const updateLiveNotifications = (updater) => {
    try {
        localStorage.setItem('notifications', JSON.stringify(updater(readLiveNotifications())));
    } catch (e) {
        console.error('Error updating stored notifications:', e);
    }
};

const RETENTION_MS = 7 * 24 * 60 * 60 * 1000;
const isWithinRetention = (notification) => (notification.timestamp || 0) >= Date.now() - RETENTION_MS;

function InAppNotification({ authUser, isOpen, onClose }) {
    const [notifications, setNotifications] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [hasLoadedHistory, setHasLoadedHistory] = useState(false);

    const organisationId = authUser && authUser.organisation && authUser.organisation._id;

    // Only pulled in on demand (View more) - notifications received live via SSE already populate the list.
    const loadHistory = useCallback(() => {
        setIsLoading(true)
        axios.get(`/notification/get-push-notifications/${authUser.email}`)
            .then(response => {
                const history = response.data.map(item => {
                    const request = item.notificationRequest || {}
                    return {
                        id: item._id,
                        title: request.subject,
                        message: request.body,
                        type: 'user',
                        metadata: {
                            name: item.name,
                            body: request.body,
                            devices: request.devices,
                            attachment: request.attachment,
                            url: request.url
                        },
                        timestamp: item.timestamp,
                        isRead: item.isRead
                    }
                })
                setNotifications(current => mergeNotifications(current, history))
                setHasLoadedHistory(true)
                setIsLoading(false)
            })
            .catch(error => {
                console.error('Error fetching notifications:', error);
                setIsLoading(false)
            });
    }, [authUser])

    // Live notifications are written to localStorage by InAppNotification/listner.js as they arrive over SSE.
    useEffect(() => {
        const syncLiveNotifications = () => {
            const scoped = readLiveNotifications().filter(n => belongsToOrg(n, organisationId))
            // Also drop any entries belonging to a previously-selected organisation.
            setNotifications(current => mergeNotifications(current.filter(n => belongsToOrg(n, organisationId)), scoped))
        }
        syncLiveNotifications()
        window.addEventListener('newNotification', syncLiveNotifications)
        return () => window.removeEventListener('newNotification', syncLiveNotifications)
    }, [organisationId])

    // Bell icon click opens the popover (isOpen -> true) - piggyback the periodic
    // localStorage cleanup here so old entries don't accumulate indefinitely.
    useEffect(() => {
        if (isOpen) {
            updateLiveNotifications(current => current.filter(isWithinRetention))
            setNotifications(current => current.filter(isWithinRetention))
        }
    }, [isOpen])

    const handleRemove = useCallback((notification) => {
        const key = notificationKey(notification)
        setNotifications(current => current.filter(n => notificationKey(n) !== key))
        updateLiveNotifications(current => current.filter(n => notificationKey(n) !== key))
        if (isPersistedNotificationId(notification.id)) {
            axios.patch(`/notification/push-notification/${notification.id}/remove`)
                .catch(error => console.error('Error removing notification:', error))
        }
    }, [])

    const handleRead = useCallback((notification) => {
        if (notification.isRead) {
            return
        }
        const key = notificationKey(notification)
        const markRead = (n) => notificationKey(n) === key ? { ...n, isRead: true } : n
        setNotifications(current => current.map(markRead))
        updateLiveNotifications(current => current.map(markRead))
        if (isPersistedNotificationId(notification.id)) {
            axios.patch(`/notification/push-notification/${notification.id}/read`)
                .catch(error => console.error('Error marking notification read:', error))
        }
    }, [])

    return (
        <Auxiliary>
            <div className="gx-popover-header">
                <h3 className="gx-mb-0">Notifications</h3>
                <div className="gx-flex-row gx-align-items-center">
                    <i
                        className="gx-icon-btn icon icon-collapse gx-notification-collapse-btn"
                        title="Hide notifications"
                        onClick={() => onClose && onClose()}
                    />
                </div>
            </div>
            <CustomScrollbars className="gx-popover-scroll">
                <ul className="gx-sub-popover">
                    {
                        notifications.length === 0 ? (
                            <div className="gx-p-3 gx-text-center">No notifications</div>
                        ) : (
                            notifications.map((notification) => <NotificationItem key={notificationKey(notification)}
                                notification={notification} onRemove={handleRemove} onRead={handleRead} onView={onClose} />)
                        )
                    }
                </ul>
                {
                    !hasLoadedHistory && (
                        <div className="gx-p-3 gx-text-center">
                            <Button size="small" loading={isLoading} onClick={loadHistory}>View more</Button>
                        </div>
                    )
                }
            </CustomScrollbars>
        </Auxiliary>
    );
}

export default InAppNotification;
